import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  BarChart3,
  Settings,
  Zap,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { isPremiumPlan } from '../../lib/plans';
import Badge from '../ui/Badge';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/expenses',  label: 'Expenses',  icon: Receipt },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
];

function NavItem({
  to,
  label,
  icon: Icon,
  onClick,
}: {
  to: string;
  label: string;
  icon: React.ElementType;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
          isActive
            ? 'bg-brand-500/15 text-brand-400 border border-brand-500/25'
            : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800',
        ].join(' ')
      }
    >
      <Icon size={18} />
      {label}
    </NavLink>
  );
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { profile } = useAuth();
  const isPremium = isPremiumPlan(profile?.plan);

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Logo */}
      <div className="px-5 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center">
            <Zap size={16} className="text-white" />
          </div>
          <span className="text-lg font-bold gradient-text">BudgetAI</span>
        </div>
        {/* Close on mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1">
        {navItems.map(item => (
          <NavItem key={item.to} {...item} onClick={onClose} />
        ))}
      </nav>

      {/* Upgrade banner (free users) */}
      {!isPremium && (
        <div className="mx-3 mb-4 p-4 rounded-xl bg-gradient-to-br from-brand-900/50 to-violet-900/50 border border-brand-500/20">
          <p className="text-xs font-semibold text-brand-300 mb-1">Upgrade to Premium</p>
          <p className="text-xs text-slate-400 mb-3">
            Unlock AI categorization, unlimited expenses & advanced analytics.
          </p>
          <NavLink
            to="/settings"
            onClick={onClose}
            className="block w-full text-center text-xs font-semibold py-2 px-3 bg-brand-600 hover:bg-brand-500 text-white rounded-lg transition-colors"
          >
            Upgrade Now
          </NavLink>
        </div>
      )}

      {/* User info */}
      {profile && (
        <div className="mx-3 mb-4 p-3 rounded-xl bg-slate-800/50 border border-slate-800 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
            {(profile.full_name ?? profile.email).charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-slate-200 truncate">
                {profile.full_name ?? 'User'}
              </p>
              <NavLink
                to="/settings"
                onClick={onClose}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/70 transition-colors"
                aria-label="Settings"
                title="Settings"
              >
                <Settings size={15} />
              </NavLink>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant={isPremium ? 'premium' : 'default'} className="text-[10px] px-1.5">
                {isPremium ? '⭐ Premium' : 'Free'}
              </Badge>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 z-30">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden animate-fade-in"
            onClick={onClose}
            aria-hidden="true"
          />
          <aside className="fixed inset-y-0 left-0 w-72 bg-slate-900 border-r border-slate-800 z-50 lg:hidden animate-slide-up">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
